import * as _ from 'lodash-es';

const baseUrl = new URL('/', location.href);

/**
 * `git:<hash>` chat refs are linked unconditionally, the same way `#123`
 * tracker-item refs are: any shape-matching token becomes a link, and an
 * invalid/nonexistent hash surfaces via the commit page's own "not found"
 * handling rather than a pre-flight check here.
 */
const GIT_REF_RE = /\bgit:([0-9a-fA-F]{7,40})\b/g;

export function createGitLinks(text?: string, project: string = '') {
    if (!text) {
        return '';
    }

    const map = getGitLinkMap(text, project);

    const ret = _.reduce(
        map,
        (carry, to, from) => {
            return carry.split(from).join(to);
        }, text
    );
    return ret;
};

export function getGitRefs(text: string): any {
    const list = text.match(GIT_REF_RE);
    return list ?
        _.uniq(list.sort()) :
        [];
}

export function getGitLink(item, project: string = '') {
    // No project context (e.g., a standalone/cross-project chatroom) means
    // there's no single repo to resolve a bare hash against -- leave as-is.
    if (!project) {
        return item;
    }

    const ref = item.match(/^git:([0-9a-fA-F]{7,40})$/);
    if (ref?.length != 2) {
        return item;
    }

    const hash = ref[1];
    const url = `${ baseUrl }project/${ project }/scm/commit/${ hash }`;

    return `<a href="${ url }" target="_blank" title="Go to commit ${ hash } (new tab)">${ item }</a>`;
}

/**
 * The commit URLs for the `git:<hash>` refs in `text`, with the offset of each
 * ref so callers can keep them in document order. Each distinct hash is only
 * returned once. Like getGitLink(), nothing is returned without a project.
 */
export function getGitPreviewUrls(text?: string, project: string = ''): { url: string, offset: number }[] {
    if (!text || !project) {
        return [];
    }

    const pattern = new RegExp(GIT_REF_RE.source, 'g');
    const seen = {};
    const ret = [];

    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
        const hash = match[1].toLowerCase();
        if (seen[hash]) {
            continue;
        }
        seen[hash] = true;
        ret.push({
            url: `${ baseUrl }project/${ project }/scm/commit/${ match[1] }`,
            offset: match.index
        });
    }

    return ret;
}

export function getGitLinkMap(text, project: string = '') {
    const list = getGitRefs(text);

    return _.reduce(
        list,
        (carry, item) => {
            carry[item] = getGitLink(item, project);
            return carry;
        },
        {}
    );
}
